import { useNavigate } from "@tanstack/react-router";
import type { CreatorTagColor } from "@unbox/api";
import { Check, Edit01, Trash01, Users01, XClose } from "@untitledui/icons";
import { useMemo, useState } from "react";
import { useBatchSaveCreatorCohorts, useCreatorCohorts } from "@/api/creator-cohorts";
import { UnboxTable } from "@/components/application/table/unbox-table";
import type { UnboxTableColumn } from "@/components/application/table/unbox-table.types";
import { Button } from "@/components/base/buttons/button";
import { TagColorNameInput } from "@/pages/creator-tags/tag-color-name-input";
import { useMerchant, useWorkspaceId } from "@/providers/MerchantProvider";
import { error, success } from "@/providers/ToastProvider";
import { pluralize } from "@/utils/text";
import { CohortBadge } from "./cohort-badge";
import { CohortRuleCell } from "./cohort-rule-cell";

type CohortListItem = NonNullable<ReturnType<typeof useCreatorCohorts>["data"]>[number];

type Draft = { name: string; color: CreatorTagColor };

const AssignedCreatorsCell = ({ cohort }: { cohort: CohortListItem }) => {
  const navigate = useNavigate();
  const merchantId = useMerchant();
  const workspaceId = useWorkspaceId();

  const label = (
    <>
      <span className="font-semibold">{cohort.assignedCount}</span>{" "}
      {cohort.assignedCount === 1 ? "creator" : "creators"}
    </>
  );

  if (cohort.assignedCount === 0) return <span>{label}</span>;

  const search = {
    classification: "__all" as const,
    includeCohorts: [{ id: cohort.id, label: cohort.name }],
  };
  const showAssigned = () =>
    workspaceId
      ? navigate({
          to: "/workspace/$workspaceId/merchant/$merchantId/creators" as string,
          params: { workspaceId, merchantId },
          search,
        })
      : navigate({ to: "/merchant/$merchantId/creators", params: { merchantId }, search });

  return (
    <button
      type="button"
      aria-label={`Show the creators assigned to ${cohort.name}`}
      className="cursor-pointer text-brand-secondary underline decoration-dotted decoration-1 underline-offset-4 transition-colors duration-100 hover:decoration-solid"
      onClick={showAssigned}
    >
      {label}
    </button>
  );
};

export const CohortList = ({ onInspectRule }: { onInspectRule: (ruleId: string) => void }) => {
  const { data: cohorts, isLoading } = useCreatorCohorts();
  const { mutateAsync: saveBatch, isPending } = useBatchSaveCreatorCohorts();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const startEdit = (cohort: CohortListItem) => {
    setDeletingId(null);
    setEditingId(cohort.id);
    setDraft({ name: cohort.name, color: cohort.color as CreatorTagColor });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft(null);
  };

  const commitEdit = async (cohort: CohortListItem) => {
    if (!draft) return;
    const name = draft.name.trim();
    if (!name) {
      error("Every cohort needs a name");
      return;
    }
    if (name === cohort.name && draft.color === cohort.color) {
      cancelEdit();
      return;
    }
    try {
      await saveBatch({ create: [], update: [{ id: cohort.id, name, color: draft.color }], delete: [] });
      success("Cohort updated");
      cancelEdit();
    } catch {}
  };

  const commitDelete = async (cohort: CohortListItem) => {
    try {
      await saveBatch({ create: [], update: [], delete: [cohort.id] });
      success("Cohort deleted");
    } catch {}
    setDeletingId(null);
  };

  const columns = useMemo<UnboxTableColumn<CohortListItem>[]>(
    () => [
      {
        id: "cohort",
        label: "Cohort",
        minWidth: "360px",
        render: (_value, cohort) =>
          editingId === cohort.id && draft ? (
            <TagColorNameInput
              color={draft.color}
              onColorChange={(color) => setDraft({ ...draft, color })}
              name={draft.name}
              onNameChange={(name) => setDraft({ ...draft, name })}
              label="Cohort name"
              fullWidth
            />
          ) : (
            <CohortBadge name={cohort.name} color={cohort.color} />
          ),
      },
      {
        id: "rule",
        label: "Rule",
        minWidth: "0px",
        render: (_value, cohort) =>
          deletingId === cohort.id ? (
            <div className="whitespace-nowrap text-sm text-utility-error-700">
              Deleting this cohort will remove it from {pluralize(cohort.assignedCount, "creator", "creators")}.
            </div>
          ) : (
            <div className="whitespace-nowrap">
              <CohortRuleCell conditions={cohort.ruleConditions} onInspect={onInspectRule} />
            </div>
          ),
      },
      {
        id: "assigned",
        label: "Creators Assigned",
        minWidth: "0px",
        render: (_value, cohort) =>
          deletingId === cohort.id ? null : (
            <div className="whitespace-nowrap text-center tabular-nums text-secondary">
              <AssignedCreatorsCell cohort={cohort} />
            </div>
          ),
      },
      {
        id: "owner",
        label: "Owner",
        minWidth: "0px",
        render: (_value, cohort) =>
          deletingId === cohort.id ? null : (
            <span className="whitespace-nowrap text-secondary">
              {[cohort.ownerFirstName, cohort.ownerLastName].filter(Boolean).join(" ") || "—"}
            </span>
          ),
      },
      {
        id: "source",
        label: "Source",
        minWidth: "0px",
        render: (_value, cohort) =>
          deletingId === cohort.id ? null : (
            <span className="whitespace-nowrap capitalize text-secondary">{cohort.source}</span>
          ),
      },
      {
        id: "row-actions",
        label: "",
        minWidth: "0px",
        render: (_value, cohort) => (
          <div className="flex items-center justify-end gap-2">
            {editingId === cohort.id ? (
              <>
                <Button color="tertiary" size="sm" onClick={cancelEdit}>
                  Cancel
                </Button>
                <Button color="primary" size="sm" isLoading={isPending} onClick={() => commitEdit(cohort)}>
                  Save
                </Button>
              </>
            ) : deletingId === cohort.id ? (
              <>
                <Button
                  color="tertiary"
                  size="sm"
                  iconLeading={XClose}
                  aria-label="Cancel delete"
                  onClick={() => setDeletingId(null)}
                />
                <Button
                  color="primary-destructive"
                  size="sm"
                  iconLeading={Check}
                  aria-label="Confirm delete"
                  isLoading={isPending}
                  onClick={() => commitDelete(cohort)}
                />
              </>
            ) : (
              <>
                <Button
                  color="tertiary"
                  size="sm"
                  iconLeading={Trash01}
                  aria-label={`Delete ${cohort.name}`}
                  onClick={() => {
                    cancelEdit();
                    if (cohort.assignedCount === 0) commitDelete(cohort);
                    else setDeletingId(cohort.id);
                  }}
                />
                <Button
                  color="tertiary"
                  size="sm"
                  iconLeading={Edit01}
                  aria-label={`Edit ${cohort.name}`}
                  onClick={() => startEdit(cohort)}
                />
              </>
            )}
          </div>
        ),
      },
    ],
    [editingId, draft, deletingId, isPending, onInspectRule],
  );

  return (
    <UnboxTable<CohortListItem>
      aria-label="Cohorts"
      className="max-h-[60vh]"
      columns={columns}
      rows={isLoading ? undefined : cohorts}
      rowId="id"
      emptyState={{ icon: Users01, title: "No cohorts yet", description: "Create one to get started." }}
    />
  );
};
